import { APP_INITIALIZER, NgModule, NgZone } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { MaterialModule } from './module/material.module';
import { ControlsModule } from 'controls';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { AppService } from './app.service';
import { DialogService } from './service/dialog.service';
import { QRCodeGenerationService } from './service/qrcode-generation.service';
import { NavigationService } from './service/navigation.service';
import { SubscriptionService } from './service/subscription.service';
import { DepartmentSelector } from './service/department-selector';
import { PassportsStoreImpl } from './service/passports-store-impl';
import { AnimationsConfigImpl } from './service/animations-config-impl';
import { ApplicationContextImpl } from './service/application-context-impl';
import { BirthplaceDictionaryImpl } from './service/birthplace-dictionary-impl';
import { MobileStorageManager } from './service/mobile-storage-manager';
import { MobileRequestDataAccessService } from './service/mobile-request-data-access.service';
import { FeaturesV2FactoryImpl } from './service/features-factory-impl';
import { FeaturesFactory, FeaturesTreeReaderFactory, PassportsStore } from 'types';
import { WizardContextImpl } from './service/wizard-context-impl';
import { ValidationProfiler } from 'validation';
import { ValidationProfilerImpl } from './service/validation-profiler-impl';
import { DeptBookService, HttpDeptBookService } from './service/deptbook.service';
import { InfoSiteService, HttpInfoSiteService } from './service/info-site.service';
import { VersionService, VersionServiceFactory } from './service/version.service';
import { NetworkService, MobileNetworkService } from './service/network.service';
import { NotificationService, NotificationServiceImpl } from './service/notification-service';
import { ImageProcessorImpl } from './service/image-processor-impl';
import { PhotoUploadCallbackImpl } from './service/photo-upload-callback-impl';
import {
    AppContext,
    AnimationsConfig,
    WizardContext,
    CommandContext,
    CommandContextImpl,
    ImageProcessor,
    PhotoUploadCallback,
    TableEditorDialogBoxRef,
    FeaturesTreeReaderFactoryImpl,
    ValidationService,
    VirtualKeyboardService,
    NullVirtualKeyboardService,
} from 'controls';
import { AppComponent } from './app.component';
import { ApplicationUpdateService } from './service/application-update.service';
import { RequestDepartmentComponent } from './view/page/request-department/request-department.component';
import { RequestEditorComponent } from './view/page/request-editor/request-editor.component';
import { RequestPreviewComponent } from './view/page/request-preview/request-preview.component';
import { DepartmentsComponent } from './view/page/departments/departments.component';
import { NotificationsComponent } from './view/page/notifications/notifications.component';
import { FeedbackComponent } from './view/page/feedback/feedback.component';
import { HelpComponent } from './view/page/help/help.component';
import { SettingsComponent } from './view/page/settings/settings.component';
import { RequestsComponent } from './view/page/requests/requests.component';
import { SubscriptionComponent } from './view/page/subscription/subscription.component';
import { NotificationIconComponent } from './view/control/notification-icon/notification-icon.component';
import { RequestQrcodeComponent } from './view/page/request-qrcode/request-qrcode.component';
import { SubscriptionViaBarcodeComponent } from './view/page/subscription-via-barcode/subscription-via-barcode.component';
import { StatusChangeNotificationComponent } from './view/control/status-change-notification/status-change-notification.component';
import { RequestUidScanningComponent } from './view/page/request-uid-scanning/request-uid-scanning.component';
import { RequestSummaryComponent } from './view/control/request-summary/request-summary.component';
import { PrintComponent } from './view/page/print/print.component';
import { LongPressDirective } from './directive/long-press.directive';
import { IBirthplaceDictionary } from 'src/webid/4.0/model/dictionaries/couchdb.birthplace.dictionary';
import { PrintQRCodeGenerationService } from 'src/webid/4.0/model/print-web/service/print-qrcode-generation.service';
import { FeaturesMobileComponent } from 'src/webid/4.0/model/features.mobile.component';
import { Features2MobileComponent } from 'src/webid/4.0/model/features2.mobile.component';
import { AdultRequestFormV1Component } from 'src/webid/4.0/model/print-web/view/adult-request-form-v1/adult-request-form-v1.component';
import { AdultRequestFormV2Component } from 'src/webid/4.0/model/print-web/view/adult-request-form-v2/adult-request-form-v2.component';
import { ChildRequestFormV1Component } from 'src/webid/4.0/model/print-web/view/child-request-form-v1/child-request-form-v1.component';
import { ChildRequestFormV2Component } from 'src/webid/4.0/model/print-web/view/child-request-form-v2/child-request-form-v2.component';
import { RequestDataAccessService } from 'data-access';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { DeviceService } from './service/device.service';
import { Observable, from } from 'rxjs';
import { Device } from '@capacitor/device';
import { CustomSnackbarComponent } from './view/control/custom-snackbar/custom-snackbar.component';
import configuration from './configuration.providers';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { CloseScrollStrategy } from '@angular/cdk/overlay';
import { ScrollDispatcher, ViewportRuler } from '@angular/cdk/scrolling';
import { MAT_TOOLTIP_SCROLL_STRATEGY } from '@angular/material/tooltip';

function initializeApp(deviceService: DeviceService): () => Observable<void> {
    const promiseGroup = Device.getInfo()
        .then((response) => {
            deviceService.initialize(response);
        })
        .catch((error) => {
            alert('Не удалось получить информацию об устройстве');
            console.error(error);
        });
    return () => from(promiseGroup);
}

@NgModule({
    declarations: [
        AppComponent,
        RequestsComponent,
        RequestEditorComponent,
        RequestPreviewComponent,
        RequestDepartmentComponent,
        DepartmentsComponent,
        NotificationsComponent,
        FeedbackComponent,
        HelpComponent,
        SettingsComponent,
        NotificationIconComponent,
        FeaturesMobileComponent,
        Features2MobileComponent,
        LongPressDirective,
        SubscriptionComponent,
        RequestQrcodeComponent,
        SubscriptionViaBarcodeComponent,
        StatusChangeNotificationComponent,
        RequestUidScanningComponent,
        RequestSummaryComponent,
        PrintComponent,
        AdultRequestFormV1Component,
        AdultRequestFormV2Component,
        ChildRequestFormV1Component,
        ChildRequestFormV2Component,
        CustomSnackbarComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        MaterialModule,
        AppRoutingModule,
        ControlsModule,
        HttpClientModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerWhenStable:5000',
        }),
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [DeviceService],
            multi: true,
        },
        ...configuration,
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
        { provide: DeviceService, useClass: DeviceService },
        { provide: AppService, useClass: AppService },
        { provide: AppContext, useClass: ApplicationContextImpl },
        { provide: DialogService, useClass: DialogService },
        { provide: QRCodeGenerationService, useClass: QRCodeGenerationService },
        { provide: InfoSiteService, useClass: HttpInfoSiteService },
        { provide: AnimationsConfig, useClass: AnimationsConfigImpl },
        { provide: CommandContext, useClass: CommandContextImpl },
        { provide: ApplicationUpdateService, useClass: ApplicationUpdateService },
        { provide: DeptBookService, useClass: HttpDeptBookService },
        { provide: DepartmentSelector, useClass: DepartmentSelector },
        { provide: SubscriptionService, useClass: SubscriptionService },
        { provide: ImageProcessor, useClass: ImageProcessorImpl },
        { provide: IBirthplaceDictionary, useClass: BirthplaceDictionaryImpl },
        { provide: MobileStorageManager, useClass: MobileStorageManager },
        { provide: NavigationService, useClass: NavigationService },
        { provide: NotificationService, useClass: NotificationServiceImpl },
        { provide: RequestDataAccessService, useClass: MobileRequestDataAccessService },
        { provide: FeaturesFactory, useClass: FeaturesV2FactoryImpl },
        { provide: FeaturesTreeReaderFactory, useClass: FeaturesTreeReaderFactoryImpl },
        { provide: TableEditorDialogBoxRef, useClass: TableEditorDialogBoxRef },
        { provide: WizardContext, useClass: WizardContextImpl },
        { provide: PassportsStore, useClass: PassportsStoreImpl },
        { provide: PhotoUploadCallback, useClass: PhotoUploadCallbackImpl },
        { provide: NetworkService, useClass: MobileNetworkService },
        { provide: PrintQRCodeGenerationService, useClass: QRCodeGenerationService },
        { provide: VersionService, useClass: VersionServiceFactory.resolve() },
        { provide: ValidationService, useClass: ValidationService },
        { provide: ValidationProfiler, useClass: ValidationProfilerImpl },
        { provide: VirtualKeyboardService, useClass: NullVirtualKeyboardService },
        { provide: MAT_DATE_LOCALE, useValue: 'ru-RU' },
        {
            provide: MAT_TOOLTIP_SCROLL_STRATEGY,
            deps: [ScrollDispatcher, NgZone, ViewportRuler],
            useFactory: (d: ScrollDispatcher, z: NgZone, r: ViewportRuler) => {
                return () => new CloseScrollStrategy(d, z, r);
            }
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
